import { observer } from "mobx-react-lite";

import { TIME_FORMATS } from "@bps/date-time";
import { ITextProps, Spinner, Stack, Text, TooltipHost } from "@bps/fluent-ui";
import { CalendarEvent } from "@stores/booking/models/CalendarEvent.ts";
import { useStores } from "@stores/hooks/useStores.ts";
import { DataFetcher } from "@ui-components/data-fetcher/DataFetcher.tsx";
import { UserFetcher } from "@ui-components/UserFetcher.tsx";

import { getAppointmentStatusFromCode } from "../utils.ts";

const TableCell: React.FunctionComponent<ITextProps> = props => (
  <Stack
    verticalAlign="center"
    styles={{ root: { height: "100%", overflow: "hidden" } }}
  >
    <Text {...props} />
  </Stack>
);

export const AppointmentInformationDate = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell>
      <Stack>{props.calendarEvent.startDateTime.toDayDefaultFormat()}</Stack>
      <Stack>
        {props.calendarEvent.startDateTime.toFormat(
          TIME_FORMATS.DEFAULT_TIME_FORMAT
        )}
      </Stack>
    </TableCell>
  )
);

export const AppointmentInformationApptType = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell>
      {props.calendarEvent.appointmentType?.text || "Not recorded"}
    </TableCell>
  )
);

export const AppointmentInformationDuration = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell styles={{ root: { textAlign: "right" } }}>
      {`${props.calendarEvent.duration.minutes} mins`}
    </TableCell>
  )
);

export const AppointmentInformationStatus = observer(
  (props: { calendarEvent: CalendarEvent }) => {
    const { booking } = useStores();
    const {
      calendarEvent: {
        appointmentStatus,
        cancellationReasonId,
        cancellationText,
        status
      }
    } = props;

    const fetch = async () => {
      const cancelledReasonId = cancellationReasonId;
      if (cancelledReasonId) {
        return await booking.getCalendarCancellationReason(cancelledReasonId);
      }
      return undefined;
    };

    return (
      <TableCell>
        {appointmentStatus && status ? (
          <DataFetcher
            fetch={fetch}
            fallback={<Spinner />}
            refetchId={cancellationReasonId}
          >
            {cancelledReason => {
              const cancelReasonText =
                cancelledReason?.text === "Other"
                  ? `${cancelledReason?.text}: ${cancellationText}`
                  : cancelledReason?.text;
              return (
                <TooltipHost content={cancelReasonText ?? ""}>
                  {getAppointmentStatusFromCode(appointmentStatus, status)}
                </TooltipHost>
              );
            }}
          </DataFetcher>
        ) : undefined}
      </TableCell>
    );
  }
);

export const AppointmentInformationProvider = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell>
      <UserFetcher userId={props.calendarEvent.userId}>
        {user => user.fullName}
      </UserFetcher>
    </TableCell>
  )
);

export const AppointmentInformationLocation = observer(
  (props: { calendarEvent: CalendarEvent }) => {
    const { core } = useStores();
    return (
      <TableCell>
        {core.getLocationName(props.calendarEvent.orgUnitId)}
      </TableCell>
    );
  }
);

export const AppointmentInformationClaim = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell>{props.calendarEvent.claim?.claimNumber}</TableCell>
  )
);

export const AppointmentCancellationReason = observer(
  (props: { calendarEvent: CalendarEvent }) => (
    <TableCell>
      {props.calendarEvent.cancellationReason?.text === "Other" ? (
        <Text>{props.calendarEvent.cancellationText}</Text>
      ) : (
        props.calendarEvent.cancellationReason?.text
      )}
    </TableCell>
  )
);
